import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Check, X, Users } from "lucide-react";

/**
 * Small button+popover for incoming friend requests. Badge count of pending
 * requests. Click opens a list with Accept/Decline. Also toggle to view
 * your friends list.
 */
export default function FriendInbox({ onOpenProfile }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("pending"); // pending | list
  const [pending, setPending] = useState([]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, l] = await Promise.all([api.friendsPending(), api.friendsList()]);
      setPending(p.pending || []);
      setList(l.friends || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const act = async (fn, id, msg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn(id);
      toast(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="friend-inbox-open"
        onClick={() => setOpen(true)}
        className="relative p-2 border border-[#333] hover:border-[#6E8B3D] text-[#6E8B3D] transition-colors"
        title="Friends"
      >
        <UserPlus className="w-4 h-4" strokeWidth={1.5} />
        {pending.length > 0 && (
          <span
            data-testid="friend-inbox-badge"
            className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-[#E33333] text-[#050505] font-mono text-[9px] rounded-full"
          >
            {pending.length}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center px-4 pb-16"
          style={{ background: "rgba(5,5,5,0.65)", backdropFilter: "blur(2px)" }}
          onClick={() => setOpen(false)}
          data-testid="friend-inbox"
        >
          <div
            className="panel p-3 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-2">
              <button
                data-testid="friend-tab-pending"
                onClick={() => setTab("pending")}
                className={`font-heading text-[10px] tracking-widest uppercase px-2 py-1 border ${tab === "pending" ? "border-[#E5C34A] text-[#E5C34A]" : "border-[#333] text-[#8c8c8c]"}`}
              >
                Requests ({pending.length})
              </button>
              <button
                data-testid="friend-tab-list"
                onClick={() => setTab("list")}
                className={`font-heading text-[10px] tracking-widest uppercase px-2 py-1 border ${tab === "list" ? "border-[#E5C34A] text-[#E5C34A]" : "border-[#333] text-[#8c8c8c]"}`}
              >
                Friends ({list.length})
              </button>
              <button
                data-testid="friend-inbox-close"
                onClick={() => setOpen(false)}
                className="ml-auto text-[#8c8c8c] hover:text-[#E2DFD8]"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            {tab === "pending" && (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {pending.length === 0 && (
                  <div className="text-center text-[#666] text-xs italic py-3">
                    No pending requests.
                  </div>
                )}
                {pending.map(r => (
                  <div
                    key={r.req_id}
                    className="flex items-center gap-2 p-2 border border-[#262626]"
                    data-testid={`friend-pending-${r.req_id}`}
                  >
                    <button
                      className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] flex-1 text-left truncate hover:text-[#E5C34A]"
                      onClick={() => { onOpenProfile?.(r.from_id); setOpen(false); }}
                    >
                      {r.from_name}
                    </button>
                    <button
                      data-testid={`friend-accept-${r.req_id}`}
                      onClick={() => act(api.friendAccept, r.req_id, "Friend added.")}
                      disabled={busy}
                      className="px-2 py-1 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[9px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-30"
                    >
                      <Check className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid={`friend-decline-${r.req_id}`}
                      onClick={() => act(api.friendDecline, r.req_id, "Request declined.")}
                      disabled={busy}
                      className="px-2 py-1 border border-[#333] text-[#666] font-heading text-[9px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-30"
                    >
                      <X className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {tab === "list" && (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {list.length === 0 && (
                  <div className="text-center text-[#666] text-xs italic py-3">
                    You have no friends yet.
                  </div>
                )}
                {list.map(f => (
                  <button
                    key={f.user_id}
                    onClick={() => { onOpenProfile?.(f.user_id); setOpen(false); }}
                    className="w-full flex items-center gap-2 p-2 border border-[#262626] hover:border-[#6E8B3D] transition-colors"
                    data-testid={`friend-${f.user_id}`}
                  >
                    <Users className="w-3 h-3 text-[#6E8B3D]" strokeWidth={1.5} />
                    <span className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] flex-1 text-left truncate">
                      {f.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
